import React, { useRef } from 'react';
import { CaretLeftIcon, CaretRightIcon } from '@phosphor-icons/react';
import { Box, IconButton, useTheme } from '@mui/material';

import SectionHeaderWithChevron from './SectionHeaderWithChevron';
import UserCardVertical from './UserCardVertical';
import { edgeToEdgeSx, scrollFadeMaskSx } from '../../utils/scrollStyles';
import { UserProfile } from '@watchlist/shared';

interface SuggestedUsersCarouselProps {
  suggestions: UserProfile[];
  currentUserId?: string;
  isUserFollowed: (userId: string) => boolean;
  onUserClick?: (userId: string) => void;
  onFollowToggle?: (userId: string) => Promise<void>;
  /** Remove this user from the suggestion strip */
  onDismissCard?: (userId: string) => void;
  /** Navigate to full “Find crew” list */
  onViewAll?: () => void;
  /** Render a more compact variant (smaller cards, tighter spacing) */
  compact?: boolean;
}

const SuggestedUsersCarousel: React.FC<SuggestedUsersCarouselProps> = ({
  suggestions,
  currentUserId,
  isUserFollowed,
  onUserClick,
  onFollowToggle,
  onDismissCard,
  onViewAll,
  compact = false,
}) => {
  const theme = useTheme();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isDark = theme.palette.mode === 'dark';

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = 280;
      const newScrollLeft =
        scrollContainerRef.current.scrollLeft +
        (direction === 'left' ? -scrollAmount : scrollAmount);
      scrollContainerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth',
      });
    }
  };

  if (suggestions.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mb: compact ? 2 : 3, position: 'relative' }}>
      <SectionHeaderWithChevron
        title="Find crew"
        size="xsmall"
        onClick={onViewAll}
        showChevron={!!onViewAll}
        sx={{ mb: 0.5 }}
      />

      <Box sx={{ position: 'relative', mt: 0 }}>
        {suggestions.length > 2 && (
          <IconButton
            onClick={() => scroll('left')}
            sx={{
              position: 'absolute',
              left: -8,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 2,
              bgcolor: isDark ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.9)',
              '&:hover': {
                bgcolor: isDark ? 'rgba(0,0,0,0.8)' : 'rgba(255,255,255,1)',
              },
              boxShadow: 2,
              display: { xs: 'none', sm: 'flex' },
            }}
          >
            <CaretLeftIcon />
          </IconButton>
        )}

        <Box
          ref={scrollContainerRef}
          sx={{
            display: 'flex',
            gap: 2,
            overflowX: 'auto',
            scrollBehavior: 'smooth',
            pt: 0.5,
            pb: 1,
            ...edgeToEdgeSx,
            ...scrollFadeMaskSx,
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
          }}
        >
          {suggestions.map((user) => (
            <Box
              key={user.uid}
              sx={{
                minWidth: compact ? { xs: 110, sm: 130 } : { xs: 140, sm: 160 },
                width: compact ? { xs: '32vw', sm: 150 } : { xs: '40vw', sm: 200 },
                maxWidth: compact ? { xs: 140, sm: 150 } : { xs: 180, sm: 200 },
                flexShrink: 0,
              }}
            >
              <UserCardVertical
                id={user.uid}
                name={user.displayName}
                avatar={user.photoURL}
                isFollowing={isUserFollowed(user.uid)}
                isCurrentUser={user.uid === currentUserId}
                showFollowButton={true}
                onDismiss={onDismissCard}
                onUserClick={onUserClick}
                onFollowToggle={onFollowToggle}
              />
            </Box>
          ))}
        </Box>

        {suggestions.length > 2 && (
          <IconButton
            onClick={() => scroll('right')}
            sx={{
              position: 'absolute',
              right: -8,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 2,
              bgcolor: isDark ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.9)',
              '&:hover': {
                bgcolor: isDark ? 'rgba(0,0,0,0.8)' : 'rgba(255,255,255,1)',
              },
              boxShadow: 2,
              display: { xs: 'none', sm: 'flex' },
            }}
          >
            <CaretRightIcon />
          </IconButton>
        )}
      </Box>
    </Box>
  );
};

export default SuggestedUsersCarousel;
